/**
 * main.ts
 * Main entry point for American Dream: Pocket Life.
 * Bootstraps PWA Service Worker, storage persistence, game state, Top HUD,
 * Navigation Dock, Toast Manager, and Phaser 3 2.5D Isometric Engine (M2).
 */

import './style.css';
import Phaser from './engine/MicroCanvasEngine';
import { PwaManager } from './pwa/PwaManager';
import { ensureStoragePersistence } from './pwa/StoragePersist';
import { storageService } from './storage/StorageService';
import { GameState } from './core/GameState';
import { TopHud } from './ui/TopHud';
import { NavigationDock } from './ui/NavigationDock';
import { ToastManager } from './ui/ToastManager';
import { EventBus } from './core/EventBus';
import { BootScene } from './scenes/BootScene';
import { HomeScene } from './scenes/HomeScene';
import { DecorCatalogModal } from './ui/Modals/DecorCatalogModal';
import type { InspectableStateId } from './ui/Modals/StateInspectModal';
import { DecorSystem } from './systems/DecorSystem';
import type { LifeEvent } from './content/LifeEventsData';
import { STATE_BY_CODE } from './content/UsAtlasBiomeRegistry';
import { isDevMode } from './core/AppEnv';
import { renderUiGouacheIcon } from './ui/Icons';

export let gameInstance: Phaser.Game | null = null;

export async function bootstrap(): Promise<void> {
  const devEnabled = isDevMode();
  console.log(`[PocketLife] Bootstrapping American Dream: Pocket Life (${devEnabled ? 'DEV' : 'PROD'})...`);

  if (devEnabled && typeof document !== 'undefined') {
    document.title = 'Pocket Life [DEV]';
    const appleTitleMeta = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (appleTitleMeta) {
      appleTitleMeta.setAttribute('content', 'Pocket Life DEV');
    }
    const appShell = document.getElementById('app-shell') || document.body;
    if (appShell && !document.getElementById('app-env-dev-badge')) {
      const badge = document.createElement('button');
      badge.id = 'app-env-dev-badge';
      badge.type = 'button';
      badge.innerHTML = `${renderUiGouacheIcon('icon_decor_palette', { size: 11 })}<span>DEV</span>`;
      badge.style.cssText = [
        'position: fixed',
        'top: calc(max(10px, env(safe-area-inset-top, 0px)) + 122px)',
        'right: max(10px, env(safe-area-inset-right, 0px))',
        'z-index: 9500',
        'display: inline-flex',
        'align-items: center',
        'gap: 3px',
        'background: linear-gradient(180deg, #FFF3C4 0%, #F4C46D 100%)',
        'color: #3B2314',
        'border: 1.6px solid #3B2314',
        'border-radius: 999px',
        'padding: 2px 7px',
        'font-size: 8.5px',
        'font-weight: 900',
        'letter-spacing: 0.04em',
        'cursor: pointer',
        'box-shadow: 0 2px 0 #3B2314, 0 4px 10px rgba(59, 35, 20, 0.25)'
      ].join(';');
      badge.addEventListener('click', (e) => {
        e.stopPropagation();
        void import('./ui/Modals/ShortcutsModal').then(({ ShortcutsModal }) => {
          ShortcutsModal.getInstance().open('simulator');
        });
      });
      appShell.appendChild(badge);
    }
  }

  // 1. Initialize Toast Manager
  const toastManager = ToastManager.getInstance('toast-container');

  // 2. Initialize PWA Service Worker
  const pwa = PwaManager.getInstance();
  pwa.registerServiceWorker();
  pwa.addListener({
    onOnlineChange: (isOnline) => {
      if (isOnline) {
        toastManager.show('Đã kết nối trực tuyến!', 'success');
      } else {
        toastManager.show('Đang ngoại tuyến. Tiến trình được lưu an toàn trên máy.', 'info');
      }
    },
    onUpdateAvailable: (reload) => {
      toastManager.show('Phiên bản mới đã sẵn sàng. Đang cập nhật...', 'info');
      setTimeout(reload, 1500);
    }
  });

  // 3. Request durable storage persistence from browser
  ensureStoragePersistence().then((persisted) => {
    console.log(`[PocketLife] Persistent storage granted: ${persisted}`);
  });

  // 4. Initialize Storage & Load Game State
  await storageService.init();
  const gameState = await storageService.loadGameState();

  // 5. Initialize GameState, Survival Engine & Passive Decor Rates
  GameState.init(gameState, storageService);
  DecorSystem.syncPassiveBuffs();
  storageService.startHeartbeat(10000);

  // 6. Mount Top HUD
  const topHud = new TopHud('top-hud-container');
  topHud.updateStats(gameState.stats);
  if (gameState.career?.currentTitle) {
    topHud.updateCareerTitle(gameState.career.currentTitle);
  }

  // 7. Mount Bottom Navigation Dock
  const navDock = new NavigationDock('nav-dock-container');

  // 7b. Install DOM Overlay Input Shield & Event Logger
  // Prevents pointerup/mouseup/touchend on HTML buttons (Modals, TopHUD, NavDock)
  // from bubbling to window where Phaser's MouseManager/TouchManager would raycast the canvas underneath.
  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    (window as unknown as { __lastDomUiEventTime?: number }).__lastDomUiEventTime = 0;

    const shieldContainers = ['modal-container', 'top-hud-container', 'nav-dock-container', 'toast-container'];
    const shieldedEvents = ['pointerdown', 'pointerup', 'mousedown', 'mouseup', 'touchstart', 'touchend'];

    for (const id of shieldContainers) {
      const el = document.getElementById(id);
      if (!el) continue;

      for (const evtName of shieldedEvents) {
        el.addEventListener(evtName, (ev: Event) => {
          (window as unknown as { __lastDomUiEventTime?: number }).__lastDomUiEventTime = Date.now();
          ev.stopPropagation();
        });
      }

      el.addEventListener('click', (ev: Event) => {
        (window as unknown as { __lastDomUiEventTime?: number }).__lastDomUiEventTime = Date.now();
        const target = ev.target as HTMLElement | null;
        const btn = target?.closest('button, [role="button"]') as HTMLElement | null;
        if (btn) {
          const label = (btn.id || btn.className || btn.textContent?.trim().slice(0, 24) || 'button').replace(/\s+/g, ' ');
          console.log(`[EventLog] [DOM Click] container=#${id} target="${label}"`);
        }
        // Allow clicking the dark backdrop of #modal-container to close the active modal cleanly
        if (id === 'modal-container' && target === el && el.classList.contains('active')) {
          console.log('[EventLog] [Modal Backdrop Click] Closing active modal');
          el.classList.remove('active', 'modal-container--counties-expanded');
          el.setAttribute('aria-hidden', 'true');
          const portal = document.getElementById('modal-portal');
          if (portal) portal.innerHTML = '';
        }
        ev.stopPropagation();
      });
    }
  }

  let desiredSceneKey: 'HomeScene' | 'MapScene' = 'HomeScene';
  let mapSceneLoadPromise: Promise<void> | null = null;

  // 8. Navigation & Modal handlers (lazy-loaded on demand to keep initial HomeScene boot lightweight)
  EventBus.on('OPEN_CAREER_HUB', () => {
    console.log('[EventLog] [EventBus] OPEN_CAREER_HUB');
    void import('./ui/Modals/CareerHubModal').then(({ CareerHubModal }) => {
      CareerHubModal.getInstance().open();
    });
  });

  EventBus.on('OPEN_PANTRY_MODAL', () => {
    console.log('[EventLog] [EventBus] OPEN_PANTRY_MODAL');
    void import('./ui/Modals/PantryStoreModal').then(({ PantryStoreModal }) => {
      PantryStoreModal.getInstance().open();
    });
  });

  EventBus.on(
    'OPEN_STATE_INSPECT',
    (
      payload:
        | InspectableStateId
        | {
            stateId: InspectableStateId;
            countyFips?: string;
            initialTab?: 'foods' | 'careers' | 'souvenir' | 'counties';
          }
    ) => {
      const stateId = typeof payload === 'string' ? payload : payload.stateId;
      const countyFips = typeof payload === 'object' && payload ? payload.countyFips : undefined;
      const initialTab = typeof payload === 'object' && payload ? payload.initialTab : undefined;
      console.log(`[EventLog] [EventBus] OPEN_STATE_INSPECT (${stateId})`);
      void import('./ui/Modals/StateInspectModal').then(({ StateInspectModal }) => {
        StateInspectModal.getInstance().open(stateId, countyFips, initialTab);
      });
    }
  );

  EventBus.on(
    'OPEN_COUNTY_MENU',
    (payload: { stateId: string; countyFips?: string | null }) => {
      const cleanCode = payload?.stateId?.trim().toUpperCase();
      if (cleanCode && Boolean(STATE_BY_CODE[cleanCode])) {
        console.log(
          `[EventLog] [EventBus] OPEN_COUNTY_MENU (${payload.stateId} -> Counties 2.5D Diorama)`
        );
        void import('./ui/Modals/StateInspectModal').then(({ StateInspectModal }) => {
          StateInspectModal.getInstance().open(
            payload.stateId,
            payload.countyFips || undefined,
            'counties'
          );
        });
      }
    }
  );

  EventBus.on('OPEN_LIFE_EVENT', (event: LifeEvent) => {
    console.log(`[EventLog] [EventBus] OPEN_LIFE_EVENT (${event.id})`);
    void import('./ui/Modals/LifeEventModal').then(({ LifeEventModal }) => {
      LifeEventModal.getInstance().open(event);
    });
  });

  EventBus.on('OPEN_DECOR_CATALOG', () => {
    console.log('[EventLog] [EventBus] OPEN_DECOR_CATALOG');
    DecorCatalogModal.getInstance().open();
  });

  EventBus.on('OPEN_SHORTCUTS_MODAL', () => {
    console.log('[EventLog] [EventBus] OPEN_SHORTCUTS_MODAL');
    void import('./ui/Modals/ShortcutsModal').then(({ ShortcutsModal }) => {
      ShortcutsModal.getInstance().open('simulator');
    });
  });

  EventBus.on('OPEN_SIMULATOR_MODAL', () => {
    console.log('[EventLog] [EventBus] OPEN_SIMULATOR_MODAL');
    void import('./ui/Modals/SimulatorModal').then(({ SimulatorModal }) => {
      SimulatorModal.getInstance().open();
    });
  });

  EventBus.on('NAVIGATE_TO_MAP', () => {
    console.log('[EventLog] [EventBus] NAVIGATE_TO_MAP');
    desiredSceneKey = 'MapScene';
    navDock.updateSceneState('MapScene');
    if (!gameInstance || gameInstance.scene.getActiveScene()?.sceneKey === 'MapScene') {
      return;
    }
    if (gameInstance.scene.getScene('MapScene')) {
      gameInstance.scene.start('MapScene');
      return;
    }
    if (!mapSceneLoadPromise) {
      mapSceneLoadPromise = import('./scenes/MapScene')
        .then(({ MapScene }) => {
          if (!gameInstance) return;
          const autoStart = desiredSceneKey === 'MapScene';
          if (!gameInstance.scene.getScene('MapScene')) {
            gameInstance.scene.add(MapScene, autoStart);
          } else if (autoStart && gameInstance.scene.getActiveScene()?.sceneKey !== 'MapScene') {
            gameInstance.scene.start('MapScene');
          }
        })
        .catch((err) => {
          mapSceneLoadPromise = null;
          console.error('[PocketLife] Failed to lazy-load MapScene:', err);
        });
    }
  });

  EventBus.on('NAVIGATE_TO_HOME', () => {
    console.log('[EventLog] [EventBus] NAVIGATE_TO_HOME');
    desiredSceneKey = 'HomeScene';
    if (typeof window !== 'undefined' && window.location?.hash?.toLowerCase() === '#map') {
      const cleanUrl = (window.location.pathname || '/') + (window.location.search || '');
      window.history?.replaceState?.(null, '', cleanUrl);
    }
    navDock.updateSceneState('HomeScene');
    if (gameInstance && gameInstance.scene.getActiveScene()?.sceneKey !== 'HomeScene') {
      gameInstance.scene.start('HomeScene');
    }
  });

  // 9. Mount Phaser 3 2.5D Isometric Canvas Engine (BootScene + HomeScene only on initial boot)
  if (typeof window !== 'undefined' && document.getElementById('game-container')) {
    const dpr = typeof window !== 'undefined' ? Math.max(2, window.devicePixelRatio || 2) : 2;
    const phaserConfig: Phaser.Types.Core.GameConfig = {
      type: Phaser.AUTO,
      parent: 'game-container',
      width: 390,
      height: 700,
      pixelArt: false,
      resolution: dpr,
      roundPixels: false,
      backgroundColor: '#2A1A12',
      scale: {
        mode: Phaser.Scale.FIT,
        autoCenter: Phaser.Scale.CENTER_BOTH,
      },
      scene: [BootScene, HomeScene],
    };

    gameInstance = new Phaser.Game(phaserConfig);
    console.log('[PocketLife] Phaser 3 2.5D Isometric Engine successfully mounted in #game-container.');
  }

  // 9b. Asynchronously check URL for Apple Shortcuts arrival trigger (?unlock=<state>) and start Auto-GPS State Detection
  void import('./systems/ArrivalSystem').then(({ ArrivalSystem }) => {
    const urlArrival = ArrivalSystem.checkUrlArrivalOnBoot();
    if (urlArrival && urlArrival.success) {
      setTimeout(() => {
        void import('./ui/Modals/ShortcutsModal').then(({ ShortcutsModal }) => {
          ShortcutsModal.getInstance().open('simulator', urlArrival);
        });
      }, 350);
    }

    ArrivalSystem.startAutoGpsStateDetection((autoRes) => {
      if (autoRes.isFirstVisit) {
        ToastManager.getInstance().show(
          `🎉 Phát hiện bạn vừa đến bang ${autoRes.stateName} ngoài đời thực! Đã tự động mở khóa Bang & nhận Kỷ vật 2.5D!`,
          'success'
        );
      } else {
        ToastManager.getInstance().show(
          `📍 Tự động Check-in tại ${autoRes.stateName}: +$${autoRes.carePackage?.bonusUSD} & +1 ${autoRes.carePackage?.foodName}!`,
          'success'
        );
      }
      void import('./ui/Modals/ShortcutsModal').then(({ ShortcutsModal }) => {
        ShortcutsModal.getInstance().open('simulator', autoRes);
      });
    });
  });

  // 10. Mount iOS Safari "Add to Home Screen" Guide Banner (if opened in mobile Safari browser tab)
  pwa.mountSafariInstallBanner(() => {
    void import('./ui/Modals/ShortcutsModal').then(({ ShortcutsModal }) => {
      ShortcutsModal.getInstance().open('guide');
    });
  });

  // 11. Handle PWA Home Screen Shortcut URL Hashes (#map, #home)
  if (typeof window !== 'undefined') {
    const applyHashRoute = () => {
      const hash = window.location.hash.toLowerCase();
      if (hash === '#map') {
        EventBus.emit('NAVIGATE_TO_MAP');
      } else if (hash === '#home') {
        EventBus.emit('NAVIGATE_TO_HOME');
      }
    };
    applyHashRoute();
    window.addEventListener('hashchange', applyHashRoute);
  }

  console.log('[PocketLife] M2 2.5D Isometric Engine, Pixel Assets & Direct Interaction successfully initialized.');
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      bootstrap().catch((err) => console.error('[PocketLife] Bootstrap error:', err));
    });
  } else {
    bootstrap().catch((err) => console.error('[PocketLife] Bootstrap error:', err));
  }
}
